import { StorageData, Bookmark, Tag, Settings, AIPlatform, QuickDialItem } from '@/types';

const isExtension = typeof window !== 'undefined' && window.location && window.location.protocol === 'chrome-extension:';
const getDefaultApiBase = () => {
  if (typeof window !== 'undefined' && window.location && !isExtension) {
    const { hostname, protocol, origin } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // 本地开发：走 vite 代理（默认到本地服务器，可用 VITE_API_TARGET 指向云端），避免跨域
      return '/api';
    }
    if (protocol === 'https:') {
      return `${origin}/api`;
    }
    return `http://${hostname}:3014/api`;
  }
  return 'http://localhost:3014/api';
};

let API_BASE = getDefaultApiBase();

// Helper to safely access localStorage
const getSafeLocalStorage = (key: string) => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage.getItem(key);
        }
    } catch (e) {
        console.warn('localStorage access failed:', e);
    }
    return null;
};

// Function to initialize API Base from localStorage
const initApiBase = () => {
    const storedApiBase = getSafeLocalStorage('webtags_api_base');
    if (storedApiBase) {
        API_BASE = storedApiBase;
    } else if (isExtension) {
        API_BASE = 'http://localhost:3014/api';
    } else {
        API_BASE = getDefaultApiBase();
    }
};

// Initialize immediately
initApiBase();

export const getApiBase = () => {
    const storedApiBase = getSafeLocalStorage('webtags_api_base');
    if (storedApiBase) return storedApiBase;
    return getDefaultApiBase();
};

export const resolveImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    
    // Get the base API URL (e.g. https://webtags.market.livedoc.cn:3014/api)
    const apiBase = getApiBase();
    // Remove '/api' to get the server root (e.g. https://webtags.market.livedoc.cn:3014)
    const serverRoot = apiBase.replace(/\/api$/, '');

    // Handle uploaded images (relative paths)
    // If we are in local mode but configured a remote server, we should use that server root
    
    if (url.startsWith('/')) {
        return `${serverRoot}${url}`;
    }
    
    // Fallback for legacy localhost URLs
    if (!isExtension && url.includes('localhost') && typeof window !== 'undefined' && window.location && window.location.hostname !== 'localhost') {
        const port = '3014'; // Assuming default port
        return url.replace(/localhost:\d+/, `${window.location.hostname}:${port}`);
    }
    
    // If it's just a filename or relative path without slash
    return `${serverRoot}/${url}`;
};

export const setApiBase = (url: string) => {
    // Remove trailing slash if present
    const cleanUrl = url.replace(/\/$/, '');
    API_BASE = cleanUrl;
    if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('webtags_api_base', cleanUrl);
    }
};

export const isLocalMode = () => {
    // Also check extension storage if window.localStorage doesn't have it (for content scripts/background)
    // But this function is synchronous, so it can only check localStorage.
    // For background script, we use chrome.storage.local directly.
    return getSafeLocalStorage('webtags_local_mode') === 'true';
};

export const setLocalMode = (enabled: boolean) => {
    if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('webtags_local_mode', String(enabled));
        // Sync to extension storage if available, so background script knows
        if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
            chrome.storage.local.set({ webtags_local_mode: String(enabled) });
        }
    }
};

const defaultSettings: Settings = {
  theme: 'system',
  language: 'zh-CN',
  searchEngine: 'google',
  backgroundImage: '',
  backgroundType: 'default',
  defaultBackgroundStyle: 'orb',
  quickDialItems: [],
  screenshotApiUrl: 'https://html2image.market.livedoc.cn/screenshot?url='
};

const defaultData: StorageData = {
  bookmarks: [],
  tags: [],
  settings: defaultSettings,
  aiPlatforms: []
};

const getLocalData = (): StorageData => {
    // Priority: Try to read from localStorage first (most up-to-date in app context)
    const json = getSafeLocalStorage('webtags_data');
    if (json) {
        try {
            return { ...defaultData, ...JSON.parse(json) };
        } catch (e) {
            console.error('Failed to parse local data', e);
        }
    }
    
    // If running in extension but localStorage is empty, try to sync from chrome.storage.local?
    // This is tricky because getLocalData is synchronous but chrome.storage.local.get is async.
    // We cannot wait for it here.
    // The app should handle initial hydration in useEffect.
    
    return defaultData;
};

// Async helper to get full data, supporting chrome.storage.local fallback
export const getFullLocalData = async (): Promise<StorageData> => {
     // 1. Try chrome.storage.local (if in extension) - THIS IS THE SOURCE OF TRUTH for extension mode
     if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
         const result = await chrome.storage.local.get(['webtags_data']);
         if (result.webtags_data) {
             if (typeof result.webtags_data === 'string') {
                 try {
                     const data = JSON.parse(result.webtags_data);
                     // Sync back to localStorage for next time (and for sync functions)
                     if (typeof window !== 'undefined' && window.localStorage) {
                         window.localStorage.setItem('webtags_data', result.webtags_data);
                     }
                     return { ...defaultData, ...data };
                 } catch (e) {}
             } else {
                  // Object format
                  const data = result.webtags_data as StorageData;
                   if (typeof window !== 'undefined' && window.localStorage) {
                         window.localStorage.setItem('webtags_data', JSON.stringify(data));
                   }
                  return { ...defaultData, ...data };
             }
         }
     }

     // 2. Try localStorage (Fallback or Web Mode)
     const json = getSafeLocalStorage('webtags_data');
     if (json) {
         try {
             return { ...defaultData, ...JSON.parse(json) };
         } catch (e) {}
     }
     
     return defaultData;
}

export const saveLocalData = async (data: Partial<StorageData>): Promise<void> => {
    // We need to read current data first.
    // Since getLocalData is synchronous and might miss chrome.storage updates, 
    // saveLocalData might overwrite new data from background with old data from localStorage.
    
    const current = await getFullLocalData();
    const newData = { ...current, ...data };
    
    if (typeof window !== 'undefined' && window.localStorage) {
        const json = JSON.stringify(newData);
        window.localStorage.setItem('webtags_data', json);
        
        // Sync to extension storage if available
        if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
             await chrome.storage.local.set({ webtags_data: json });
        }
    }
};

export const getStorage = async (): Promise<StorageData> => {
  if (isLocalMode()) {
    return getFullLocalData();
  }
  try {
    const apiBase = getApiBase();
    const res = await fetch(`${apiBase}/data`);
    if (res.ok) {
      const data = await res.json();
      return {
        ...defaultData,
        ...data
      };
    }
  } catch (error) {
    console.error('Failed to fetch from server, falling back to local cache', error);
    // 服务端不可达时回退到本地缓存，避免主题等设置丢失（回退到默认值会导致主题变回"跟随系统"）
    return getFullLocalData();
  }
  return defaultData;
};

export const setStorage = async (data: Partial<StorageData>): Promise<void> => {
  // Not used directly in new architecture, specific save functions are preferred
  // But keeping for compatibility if needed, though it maps poorly to granular updates
};

export const getBookmarks = async (): Promise<Bookmark[]> => {
  const data = await getStorage();
  return data.bookmarks;
};

export const saveBookmarks = async (bookmarks: Bookmark[]): Promise<void> => {
  if (isLocalMode()) {
    await saveLocalData({ bookmarks });
    return;
  }
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/data/bookmarks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(bookmarks)
  });
  if (!res.ok) {
    throw new Error(`Save bookmarks failed: ${res.status} ${res.statusText}`);
  }
};

export const getTags = async (): Promise<Tag[]> => {
  const data = await getStorage();
  return data.tags;
};

export const saveTags = async (tags: Tag[]): Promise<void> => {
  if (isLocalMode()) {
    await saveLocalData({ tags });
    return;
  }
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/data/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(tags)
  });
  if (!res.ok) {
    throw new Error(`Save tags failed: ${res.status} ${res.statusText}`);
  }
};

export const getSettings = async (): Promise<Settings> => {
  const data = await getStorage();
  return data.settings;
};

export const saveSettings = async (settings: Settings): Promise<void> => {
  if (isLocalMode()) {
    await saveLocalData({ settings });
    return;
  }
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/data/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings)
  });
  if (!res.ok) {
    throw new Error(`Save settings failed: ${res.status} ${res.statusText}`);
  }
};

export const saveAIPlatforms = async (platforms: AIPlatform[]): Promise<void> => {
  if (isLocalMode()) {
    await saveLocalData({ aiPlatforms: platforms });
    return;
  }
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/data/aiPlatforms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(platforms)
  });
  if (!res.ok) {
    throw new Error(`Save AI platforms failed: ${res.status} ${res.statusText}`);
  }
};

export const saveQuickDialItems = async (items: QuickDialItem[]): Promise<void> => {
  const settings = await getSettings();
  const newSettings = { ...settings, quickDialItems: items };
  await saveSettings(newSettings);
};
